/**
 * Otimizador de Alocação (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_otimizador.py).
 *
 * Distribui uma oferta entre as carteiras ADM ativas (visíveis ao usuário) com o
 * AllocationEngine do persevera_tools. O universo de clientes (filtros, inclusive
 * excluir officers) fica na URL e mostra na hora quantos clientes entram; parâmetros e
 * ativos são editados num rascunho e só valem ao clicar em "Executar alocação" (vão para
 * a URL em `cfg`, junto com o universo usado). O cálculo roda em segundo plano no
 * servidor (pode levar ~1 min em "Maximizar clientes"). A classe do ativo (antes/após)
 * é informativa: não restringe a alocação.
 */
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ErroApi, apiGet, apiPost, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { Cronometro } from '../../components/Cronometro';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';
import { INTERVALO_CONSULTA } from '../quant/FactorInvesting';

const CAMINHO = '/carteiras-administradas/otimizador-alocacao';
const MODOS = ['Cotas (discreto)', 'Valor total (contínuo)'] as const;

export interface Ativo {
  ticker: string;
  modo: (typeof MODOS)[number];
  cotas: number | null;
  pu: number | null;
  valor: number | null;
  emissor: string | null;
}
export interface Parametros {
  objetivo: string;
  min_pct: number;
  max_pct: number;
  max_emissor_pct: number;
  caixa_min_pct: number;
  considerar_existente: boolean;
  topup: boolean;
  metodo_topup: string;
}
interface Universo {
  officers: string[];
  tipos_cliente: string[];
  custodiantes: string[];
  excluir: string[];
  excluir_officers: string[];
}
interface Config {
  filtros: Universo;
  parametros: Parametros;
  ativos: Ativo[];
}

// Os mesmos padrões do legado.
export const PARAMETROS_PADRAO: Parametros = {
  objetivo: 'max_clients',
  min_pct: 1.5,
  max_pct: 2,
  max_emissor_pct: 5,
  caixa_min_pct: 5,
  considerar_existente: true,
  topup: true,
  metodo_topup: 'proportional',
};
export const ATIVOS_PADRAO: Ativo[] = [
  { ticker: 'CRA022008NF', modo: MODOS[0], cotas: 18, pu: 1059.38, valor: null, emissor: null },
  { ticker: 'M8CREDIT', modo: MODOS[1], cotas: null, pu: null, valor: 59850.3, emissor: null },
];

interface Opcoes {
  officers: string[];
  tipos_cliente: string[];
  custodiantes: string[];
  carteiras: string[];
  objetivos: Record<string, string>;
  metodos_topup: Record<string, string>;
}
interface Resultado {
  kpis: KpiApi[];
  avisos: string[];
  resumo: string;
  tabela: TabelaApi | null;
}
interface Estado {
  status: 'rodando' | 'pronto' | 'erro';
  iniciado_em?: string;
  mensagem?: string;
  aviso?: string;
  resultado?: Resultado;
}

function universoDe(bruto: Partial<Universo> | undefined): Universo {
  return {
    officers: bruto?.officers ?? [],
    tipos_cliente: bruto?.tipos_cliente ?? [],
    custodiantes: bruto?.custodiantes ?? [],
    excluir: bruto?.excluir ?? [],
    excluir_officers: bruto?.excluir_officers ?? [],
  };
}

function lerConfig(texto: string): Config | null {
  if (!texto) return null;
  try {
    const cfg = JSON.parse(texto) as Config;
    cfg.filtros = universoDe(cfg.filtros);
    return cfg;
  } catch {
    return null;
  }
}

const lista = (s: string) => (s ? s.split(',') : []);
const numero = (v: string | number) => (typeof v === 'number' ? v : null);
const opcoesDe = (r: Record<string, string>) =>
  Object.entries(r).map(([value, label]) => ({ value, label }));

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

function EditorAtivos({
  ativos,
  emissores,
  aoMudar,
}: {
  ativos: Ativo[];
  emissores: Record<string, string | null>;
  aoMudar: (a: Ativo[]) => void;
}) {
  const mudar = (i: number, m: Partial<Ativo>) =>
    aoMudar(ativos.map((a, j) => (j === i ? { ...a, ...m } : a)));
  return (
    <Stack gap="xs">
      <Table withTableBorder verticalSpacing={4}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Ticker</Table.Th>
            <Table.Th>Modo</Table.Th>
            <Table.Th>Cotas</Table.Th>
            <Table.Th>PU (R$)</Table.Th>
            <Table.Th>Valor total (R$)</Table.Th>
            <Table.Th>Emissor</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ativos.map((a, i) => {
            const discreto = a.modo === MODOS[0];
            return (
              <Table.Tr key={i}>
                <Table.Td>
                  <TextInput
                    aria-label="Ticker"
                    value={a.ticker}
                    onChange={(e) => mudar(i, { ticker: e.currentTarget.value })}
                    w={150}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    aria-label="Modo"
                    data={[...MODOS]}
                    value={a.modo}
                    onChange={(v) => v && mudar(i, { modo: v as Ativo['modo'] })}
                    allowDeselect={false}
                    w={200}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    aria-label="Cotas"
                    value={a.cotas ?? ''}
                    onChange={(v) => mudar(i, { cotas: numero(v) })}
                    disabled={!discreto}
                    min={1}
                    allowDecimal={false}
                    thousandSeparator="."
                    decimalSeparator=","
                    w={100}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    aria-label="PU (R$)"
                    value={a.pu ?? ''}
                    onChange={(v) => mudar(i, { pu: numero(v) })}
                    disabled={!discreto}
                    min={0.01}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    w={130}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    aria-label="Valor total (R$)"
                    value={a.valor ?? ''}
                    onChange={(v) => mudar(i, { valor: numero(v) })}
                    disabled={discreto}
                    min={0.01}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    w={150}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Emissor"
                    value={a.emissor ?? ''}
                    placeholder={emissores[a.ticker.trim()] ?? ''}
                    onChange={(e) => mudar(i, { emissor: e.currentTarget.value || null })}
                    w={220}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label="Remover ativo"
                    onClick={() => aoMudar(ativos.filter((_, j) => j !== i))}
                  >
                    ×
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Group>
        <Button
          variant="light"
          size="xs"
          onClick={() =>
            aoMudar([
              ...ativos,
              { ticker: '', modo: MODOS[0], cotas: null, pu: null, valor: null, emissor: null },
            ])
          }
        >
          Adicionar ativo
        </Button>
      </Group>
    </Stack>
  );
}

function CampoPct({
  rotulo,
  valor,
  aoMudar,
  passo,
  ajuda,
}: {
  rotulo: string;
  valor: number;
  aoMudar: (v: number) => void;
  passo: number;
  ajuda?: string;
}) {
  return (
    <NumberInput
      label={rotulo}
      description={ajuda}
      value={valor}
      onChange={(v) => aoMudar(typeof v === 'number' ? v : 0)}
      min={0}
      max={100}
      step={passo}
      decimalScale={2}
      decimalSeparator=","
      w={LARGURA.numero + 60}
    />
  );
}

export function OtimizadorAlocacao() {
  const [f, definir] = useFiltrosUrl({
    officers: '',
    tipos: '',
    custodiantes: '',
    excluir: '',
    excluir_officers: '',
    cfg: '',
  });
  const universo: Universo = {
    officers: lista(f.officers),
    tipos_cliente: lista(f.tipos),
    custodiantes: lista(f.custodiantes),
    excluir: lista(f.excluir),
    excluir_officers: lista(f.excluir_officers),
  };
  const executada = lerConfig(f.cfg);
  const [parametros, setParametros] = useState<Parametros>(
    () => executada?.parametros ?? PARAMETROS_PADRAO,
  );
  const [ativos, setAtivos] = useState<Ativo[]>(() => executada?.ativos ?? ATIVOS_PADRAO);
  const p = (m: Partial<Parametros>) => setParametros((atual) => ({ ...atual, ...m }));

  const consultaUniverso = useQuery({
    queryKey: ['carteiras.otimizador.universo', universo],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ opcoes: Opcoes; n_clientes: number; n_carteiras: number }>>(
        CAMINHO,
        { ...universo },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const o = consultaUniverso.data?.data;

  const tickers = useMemo(
    () => [...new Set(ativos.map((a) => a.ticker.trim()).filter(Boolean))].sort(),
    [ativos],
  );
  const consultaEmissores = useQuery({
    queryKey: ['carteiras.otimizador.emissores', tickers],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ emissores: Record<string, string | null> }>>(
        `${CAMINHO}/emissores`,
        { tickers },
        signal,
      ),
    enabled: tickers.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 60 * 1000,
  });
  const emissores = consultaEmissores.data?.data.emissores ?? {};
  // O que vai para a API (e para a URL): emissor em branco é preenchido no servidor pelo
  // cadastro, como o editor do legado; aqui ele só aparece em cinza.
  const ativosEnvio = ativos
    .filter((a) => a.ticker.trim())
    .map((a) => ({ ...a, ticker: a.ticker.trim(), emissor: a.emissor?.trim() || null }));
  const semEmissor =
    // Só depois de o cadastro responder (antes disso todo ativo pareceria sem emissor).
    parametros.max_emissor_pct > 0 && consultaEmissores.isSuccess && !consultaEmissores.isFetching
      ? ativosEnvio.filter((a) => !a.emissor && !emissores[a.ticker]).map((a) => a.ticker)
      : [];

  const consulta = useQuery({
    queryKey: ['carteiras.otimizador', executada],
    queryFn: ({ signal }) => apiPost<Envelope<Estado>>(CAMINHO, executada, signal),
    enabled: !!executada,
    refetchIntervalInBackground: true,
    refetchInterval: (q) => (q.state.data?.data.status === 'rodando' ? INTERVALO_CONSULTA : false),
  });
  const estado = consulta.data?.data;
  const r = estado?.status === 'pronto' ? estado.resultado : undefined;
  const alterado =
    !!executada &&
    JSON.stringify(executada) !==
      JSON.stringify({ filtros: universo, parametros, ativos: ativosEnvio });

  const executar = () =>
    definir({ cfg: JSON.stringify({ filtros: universo, parametros, ativos: ativosEnvio }) });

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Otimizador de Alocação"
        descricao="Distribui uma oferta (um ou mais ativos) entre as carteiras administradas: maximizando clientes ou volume, com limites de exposição por ativo e por emissor e caixa mínimo."
        dadosAte={consultaUniverso.data?.meta.dados_ate}
        fonte="Fibery (posições, política, officers, cadastro de ativos) · persevera_tools (AllocationEngine)"
      />
      <BarraFiltros carregando={consultaUniverso.isFetching}>
        <GrupoFiltros titulo="Universo de clientes">
          <MultiSelect
            label="Officer"
            placeholder={universo.officers.length ? '' : 'Todos'}
            data={o?.opcoes.officers ?? []}
            value={universo.officers}
            onChange={(v) => definir({ officers: v.join(',') })}
            searchable
            w={LARGURA.selecao}
          />
          <MultiSelect
            label="Excluir officers"
            // description="Sai mesmo se também estiver em Officer."
            placeholder={universo.excluir_officers.length ? '' : 'Nenhum'}
            data={o?.opcoes.officers ?? []}
            value={universo.excluir_officers}
            onChange={(v) => definir({ excluir_officers: v.join(',') })}
            searchable
            w={LARGURA.selecao}
          />
          <MultiSelect
            label="Tipo de cliente"
            placeholder={universo.tipos_cliente.length ? '' : 'Todos'}
            data={o?.opcoes.tipos_cliente ?? ['PF', 'PJ']}
            value={universo.tipos_cliente}
            onChange={(v) => definir({ tipos: v.join(',') })}
            w={LARGURA.numero + 30}
          />
          <MultiSelect
            label="Instituição (custodiante)"
            placeholder={universo.custodiantes.length ? '' : 'Todas'}
            data={o?.opcoes.custodiantes ?? []}
            value={universo.custodiantes}
            onChange={(v) => definir({ custodiantes: v.join(',') })}
            w={LARGURA.selecao}
          />
          <MultiSelect
            label="Excluir carteiras"
            data={o?.opcoes.carteiras ?? []}
            value={universo.excluir}
            onChange={(v) => definir({ excluir: v.join(',') })}
            searchable
            w={LARGURA.selecao}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[consultaUniverso.data?.meta.avisos]} />
      {consultaUniverso.isError && (
        <Alert color="red">
          {mensagem(consultaUniverso.error, 'Não foi possível carregar as carteiras.')}
        </Alert>
      )}
      {o && (
        <Text size="sm" c="dimmed">
          {o.n_clientes} clientes no universo selecionado · {o.n_carteiras} carteiras no snapshot
        </Text>
      )}

      <Secao titulo="Parâmetros de alocação">
        <Group align="flex-start">
          <Select
            label="Objetivo"
            description="Clientes: oferta pequena, mais clientes. Volume: oferta grande."
            data={opcoesDe(
              o?.opcoes.objetivos ?? {
                max_clients: 'Maximizar clientes',
                max_volume: 'Maximizar volume',
              },
            )}
            value={parametros.objetivo}
            onChange={(v) => v && p({ objetivo: v })}
            allowDeselect={false}
            w={LARGURA.selecao}
          />
          <CampoPct
            rotulo="Exposição mínima (% PL)"
            valor={parametros.min_pct}
            aoMudar={(v) => p({ min_pct: v })}
            passo={0.1}
          />
          <CampoPct
            rotulo="Exposição máxima (% PL)"
            valor={parametros.max_pct}
            aoMudar={(v) => p({ max_pct: v })}
            passo={0.1}
          />
          <CampoPct
            rotulo="Máx. por emissor (% PL)"
            ajuda="0 desabilita"
            valor={parametros.max_emissor_pct}
            aoMudar={(v) => p({ max_emissor_pct: v })}
            passo={0.5}
          />
          <CampoPct
            rotulo="Caixa mínimo pós (% PL)"
            valor={parametros.caixa_min_pct}
            aoMudar={(v) => p({ caixa_min_pct: v })}
            passo={0.5}
          />
          <Stack gap={8} mt={4}>
            <Checkbox
              label="Considerar posição existente"
              checked={parametros.considerar_existente}
              onChange={(e) => p({ considerar_existente: e.currentTarget.checked })}
            />
            <Checkbox
              label="Distribuir remanescente (top-up)"
              checked={parametros.topup}
              onChange={(e) => p({ topup: e.currentTarget.checked })}
            />
            <Select
              aria-label="Método de top-up"
              data={opcoesDe(
                o?.opcoes.metodos_topup ?? { proportional: 'Proporcional', equal: 'Igualitário' },
              )}
              value={parametros.metodo_topup}
              onChange={(v) => v && p({ metodo_topup: v })}
              disabled={!parametros.topup}
              allowDeselect={false}
              w={LARGURA.selecao - 40}
            />
          </Stack>
        </Group>
      </Secao>

      <Secao
        titulo="Ativos disponíveis para alocação"
        legenda="O emissor vem do cadastro do Fibery (aparece em cinza); digite para substituir."
      >
        <EditorAtivos ativos={ativos} emissores={emissores} aoMudar={setAtivos} />
        {semEmissor.length > 0 && (
          <Alert color="yellow" mt="sm">
            Limite por emissor ativo, mas sem emissor para: {semEmissor.join(', ')}. Esses ativos
            ignorarão o teto de emissor.
          </Alert>
        )}
        <Group mt="md">
          <Button
            color="dourado"
            onClick={executar}
            disabled={!ativosEnvio.length || (!!executada && !alterado)}
          >
            Executar alocação
          </Button>
        </Group>
      </Secao>

      {executada && consulta.isPending && <Loader />}
      {consulta.isError && (
        <Alert color="red">
          {mensagem(consulta.error, 'Não foi possível executar a alocação.')}
        </Alert>
      )}
      {alterado && estado && (
        <Alert color="blue">
          Parâmetros alterados. Clique em <b>Executar alocação</b> para recalcular.
        </Alert>
      )}
      {!alterado && estado?.status === 'rodando' && (
        <Alert color="blue" icon={<Loader size="sm" />} title="Calculando a alocação">
          Iniciado em {formatarDataHora(estado.iniciado_em)} (há{' '}
          <Cronometro desde={estado.iniciado_em ?? ''} />
          ). Em &quot;Maximizar clientes&quot; pode levar cerca de 1 minuto; o resultado aparece
          aqui sozinho.
        </Alert>
      )}
      {!alterado && estado?.status === 'erro' && (
        <Alert color="red" title="A alocação falhou">
          {estado.mensagem}
        </Alert>
      )}
      {!alterado && estado?.aviso && <Alert color="yellow">{estado.aviso}</Alert>}
      {!alterado && r && (
        <Secao
          titulo="Resultado"
          legenda="Classe antes e após é o peso da classe do ativo no PL, somando o que esta rodada alocou nessa classe. Não entra como restrição."
        >
          <Stack>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {r.kpis.map((k) => (
                <Kpi key={k.rotulo} kpi={k} />
              ))}
            </SimpleGrid>
            <AvisosApi avisos={[r.avisos]} />
            <Code block>{r.resumo}</Code>
            {r.tabela ? (
              <TabelaDados tabela={r.tabela} alturaAutomatica />
            ) : (
              <Alert color="blue">Nenhuma alocação gerada com os parâmetros informados.</Alert>
            )}
          </Stack>
        </Secao>
      )}
    </Pagina>
  );
}
